import classNames from 'classnames';
import { Button } from 'components/buttons';
import { FormReadonly } from 'components/form-fields/FormReadonly';
import { StatusHeader } from 'components/headers';
import { ShowHide } from 'components/layout';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { OcrNumberOfNumberLayout, OcrSelectLayout, OcrStringLayout, OcrDateRangeLayout, OcrTableLayout, OcrVarianceTableLayout, OcrIntegerLayout, OcrFixedPercentageRedGreen, OcrDateRangeEditableLayout } from './layout-types';
import { OcrDateLayout } from './layout-types/OcrDateLayout';
import { OcrTextareaLayout } from './layout-types/OcrTextareaLayout';
import { OcrComplianceTableLayout } from './layout-types/OcrComplianceTableLayout';

export function OcrLayout(
    {
        loading = false,
        disabled = false,
        readonly = false,
        previsit = {},
        assets,
        employees,
        layout,
        onChange,
        status,
        statusTooltip
    }
) {
    const [tableExpanded, setTableExpanded] = useState(false);

    const tableExpanderThreshold = 50;

    const tableLayoutTypes = [
        'TABLE',
        'VARIANCETABLE',
        'VARIANCETABLETWOROWSPERCENTAGE',
        'VARIANCETABLEPERCENTAGENONCOMPARATIVE'
    ]

    const booleanSelectValues = [
        {
            id: "true",
            name: "Yes"
        },
        {
            id: "false",
            name: "No"
        }
    ];

    const booleanNaSelectValues = [
        ...booleanSelectValues,
        {
            id: "na",
            name: "N/A"
        }
    ];

    const booleanEvidencedValues = [
        {
            id: "true",
            name: "Evidenced"
        },
        {
            id: "false",
            name: "Discussed"
        },
        {
            id: "na",
            name: "Unavailable"
        }
    ];

    const booleanEvidencedNaValues = [
        {
            id: "true",
            name: "Evidenced"
        },
        {
            id: "false",
            name: "Discussed"
        },
        {
            id: "Unavailable",
            name: "Unavailable"
        },
        {
            id: "na",
            name: "N/A"
        }
    ];

    const manualEntriesDropdown = [
        {
            id: "NO",
            name: "No"
        },
        {
            id: "BYSITE",
            name: "Entries made by site"
        },
        {
            id: "ANALOGUECHARTS",
            name: "Entries made using analogue charts"
        },
        {
            id: "LOGISTICSUK",
            name: "Entries made by Logistics UK"
        },
        {
            id: "MIXEDMETHODS",
            name: "Entries made using mixed methods"
        }
    ];

    const editableColumnTypes = [
        "DRIVERNAME",
        "DATEPAST",
        "BOOLEAN",
        "STRING",
        "INTEGER",
        "BOOLEANNA",
        "BOOLEANEVIDENCED",
        "BOOLEANEVIDENCEDNA",
        "TABLEBOOLEANSELECT"
    ];

    function renderLayoutType() {
        if (layout.layoutType) {
            switch (layout.layoutType.toUpperCase()) {
                case 'TABLE':
                    return (
                        <OcrTableLayout
                            layout={layout}
                            loading={loading}
                            onChange={onChange}
                            employeeList={employees}
                            assetList={assets}
                            disabled={disabled}
                            booleanSelectValues={booleanSelectValues}
                            booleanNaSelectValues={booleanNaSelectValues}
                            editableColumnTypes={editableColumnTypes}
                            getColumnTypeForCell={getColumnTypeForCell}
                            readonly={readonly}
                            tableExpanded={tableExpanded}
                            tableExpandable={tableIsExpandable()}
                            previsit={previsit}
                        />
                    );
                case 'COMPTABLE':
                    return (
                        <OcrComplianceTableLayout
                            layout={layout}
                            loading={loading}
                            onChange={onChange}
                            employeeList={employees}
                            assetList={assets}
                            disabled={disabled}
                            booleanSelectValues={booleanSelectValues}
                            booleanNaSelectValues={booleanNaSelectValues}
                            editableColumnTypes={editableColumnTypes}
                            getColumnTypeForCell={getColumnTypeForCell}
                            readonly={readonly}
                            tableExpanded={tableExpanded}
                            tableExpandable={tableIsExpandable()}
                            previsit={previsit}
                        />
                    );
                case 'DATERANGE':
                    return (
                        <OcrDateRangeLayout
                            layout={layout}
                            loading={loading}
                            disabled={disabled}
                            readonly={readonly}
                        />
                    );
                case 'DATERANGEEDITABLE':
                    return (
                        <OcrDateRangeEditableLayout
                            layout={layout}
                            loading={loading}
                            disabled={disabled}
                            onChange={onChange}
                            readonly={readonly}
                        />
                    );
                case 'STRING':
                    return (
                        <OcrStringLayout
                            layout={layout}
                            loading={loading}
                            onChange={onChange}
                            disabled={disabled}
                            readonly={readonly}
                        />
                    );
                case 'TEXTAREA':
                    return (
                        <OcrTextareaLayout
                            layout={layout}
                            loading={loading}
                            onChange={onChange}
                            disabled={disabled}
                            readonly={readonly}
                        />
                    );
                case 'VARIANCETABLE':
                    return (
                        <OcrVarianceTableLayout
                            layout={layout}
                            loading={loading}
                            onChange={onChange}
                            disabled={disabled}
                            readonly={readonly}
                            tableExpanded={tableExpanded}
                            tableExpandable={tableIsExpandable()}
                        />
                    );
                case 'VARIANCETABLETWOROWSPERCENTAGE':
                    return (
                        <OcrVarianceTableLayout
                            layout={layout}
                            loading={loading}
                            onChange={onChange}
                            disabled={disabled}
                            addPercentageRow={true}
                            readonly={readonly}
                            tableExpanded={tableExpanded}
                            tableExpandable={tableIsExpandable()}
                        />
                    );
                case 'VARIANCETABLEPERCENTAGENONCOMPARATIVE':
                    return (
                        <OcrVarianceTableLayout
                            layout={layout}
                            loading={loading}
                            onChange={onChange}
                            disabled={disabled}
                            addPercentageRow={true}
                            readonly={readonly}
                            tableExpanded={tableExpanded}
                            tableExpandable={tableIsExpandable()}
                            showVariance={false}
                        />
                    );
                case 'BOOLEAN':
                    return (
                        <OcrSelectLayout
                            layout={layout}
                            loading={loading}
                            onChange={onChange}
                            items={booleanSelectValues}
                            placeholder='Choose Yes or No'
                            disabled={disabled}
                            readonly={readonly}
                        />
                    );
                case 'BOOLEANNA':
                    return (
                        <OcrSelectLayout
                            layout={layout}
                            loading={loading}
                            onChange={onChange}
                            items={booleanNaSelectValues}
                            placeholder='Choose Yes, No or N/A'
                            disabled={disabled}
                            readonly={readonly}
                        />
                    );
                case 'BOOLEANEVIDENCED':
                    return (
                        <OcrSelectLayout
                            layout={layout}
                            loading={loading}
                            onChange={onChange}
                            items={booleanEvidencedValues}
                            placeholder='Choose Evidenced, Discussed or Unavailable'
                            disabled={disabled}
                            readonly={readonly}
                        />
                    );
                case 'BOOLEANEVIDENCEDNA':
                    return (
                        <OcrSelectLayout
                            layout={layout}
                            loading={loading}
                            onChange={onChange}
                            items={booleanEvidencedNaValues}
                            placeholder='Choose Evidenced, Discussed, Unavailable or N/A'
                            disabled={disabled}
                            readonly={readonly}
                        />
                    );
                case 'MANUALENTRIESDROPDOWN':
                    return (
                        <OcrSelectLayout
                            layout={layout}
                            loading={loading}
                            onChange={onChange}
                            items={manualEntriesDropdown}
                            disabled={disabled}
                            readonly={readonly}
                        />
                    );
                case 'DATE':
                    return (
                        <OcrDateLayout
                            layout={layout}
                            loading={loading}
                            onChange={onChange}
                            disabled={disabled}
                            readonly={readonly}
                        />
                    );
                case 'NUMBEROFNUMBER':
                    return (
                        <OcrNumberOfNumberLayout
                            layout={layout}
                            loading={loading}
                            onChange={onChange}
                            disabled={disabled}
                            readonly={readonly}
                            firstNumberHeading={layout?.table?.columns.length > 1 ? layout.table.columns[0]?.columnTitle : ''}
                            secondNumberHeading={layout?.table?.columns.length > 1 ? layout.table.columns[1]?.columnTitle : ''}
                        />
                    );
                case 'FIXEDSTRING':
                    return (
                        <FormReadonly
                            headingText={layout.secondaryLayoutTitle ? layout.secondaryLayoutTitle : ''}
                            value={layout.answer}
                            loading={loading}
                            largerValueText={!layout.secondaryLayoutTitle}
                        />
                    );
                case 'INTEGER':
                    return (
                        <OcrIntegerLayout
                            layout={layout}
                            loading={loading}
                            onChange={onChange}
                            disabled={disabled}
                            readonly={readonly}
                        />
                    );
                case 'FIXEDPERCENTAGEREDGREEN':
                    return (
                        <OcrFixedPercentageRedGreen
                            layout={layout}
                            loading={loading}
                            onChange={onChange}
                            disabled={disabled}
                            readonly={readonly}
                        />
                    );
            }
        }

        return <div>Layout not supported</div>;
    }

    function getColumnTypeForCell(cellIndex) {
        const columnTypes = [];

        for (let i = 0; i < layout.table.columns.length; i++) {
            const column = layout.table.columns[i];

            const colspan = column.colspan ? column.colspan : 1;

            for (let j = 0; j < colspan; j++) {
                columnTypes.push(column.columnType);
            }
        }

        return columnTypes[cellIndex];
    }

    function expandTable() {
        setTableExpanded(!tableExpanded);
    }

    function tableIsExpandable() {
        const layoutType = layout.layoutType?.toUpperCase();

        return tableLayoutTypes.includes(layoutType) && layout?.table?.rows?.length > tableExpanderThreshold;
    }

    return (
        <div className='spacer-bottom print-page-break-avoid'>
            <ShowHide
                evaluator={!!layout.layoutTitle}
                show={(
                    <div className={classNames({ 'inline-items spacer-bottom-small  inline-items-space': tableIsExpandable() })} >
                        <StatusHeader
                            text={layout.layoutTitle}
                            status={status}
                            showStatus={layout.businessLogicRequired}
                            secondaryHeader={true}
                            tooltip={statusTooltip}
                            className={classNames({ 'inline-item': tableIsExpandable() })}
                        />
                        <ShowHide
                            evaluator={tableIsExpandable()}
                            show={<Button
                                text={tableExpanded ? 'Collapse table' : 'Expand table'}
                                intent="primary"
                                className='inline-item'
                                icon={tableExpanded ? "minimize" : "maximize"}
                                onClick={expandTable}
                            />}
                        />
                    </div>
                )}
            />
            {renderLayoutType()}
        </div>
    );
}

OcrLayout.propTypes = {
    loading: PropTypes.bool,
    layout: PropTypes.object.isRequired,
    status: PropTypes.string.isRequired,
    statusTooltip: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    employees: PropTypes.array.isRequired,
    assets: PropTypes.array.isRequired,
    disabled: PropTypes.bool,
    readonly: PropTypes.bool,
    previsit: PropTypes.object,
    filterOn: PropTypes.bool
};
